export function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname || '/';
}

export function isPath(pathname: string, target: string): boolean {
  return normalizePathname(pathname) === normalizePathname(target);
}

export function isPathPrefix(pathname: string, prefix: string): boolean {
  const path = normalizePathname(pathname);
  const base = normalizePathname(prefix);
  return path === base || path.startsWith(`${base}/`);
}

export const clientPaths = {
  home: '/',
  myCommunity: '/my-community',
  event: {
    list: '/event',
    create: '/event/create',
    edit: (slug: string) => `/event/edit/${slug}`,
    dashboard: (slug: string) => `/event/dashboard/${slug}`,
    checkout: (orderNumber?: string) =>
      orderNumber ? `/event/checkout/${orderNumber}` : '/event/checkout',
    detail: (slug: string) => `/event/${slug}`,
    participants: (slug: string) => `/event/${slug}/participants`,
    statistik: (slug: string) => `/event/${slug}/statistik`,
    badminton: (slug: string) => `/event/${slug}/badminton`,
    badmintonTambahGame: (slug: string) => `/event/${slug}/badminton/tambah-game`,
    badmintonEditSkor: (slug: string, gameSlug: string) =>
      `/event/${slug}/badminton/game/${gameSlug}/skor`,
    padel: (slug: string) => `/event/${slug}/padel`,
    padelTambahGame: (slug: string) => `/event/${slug}/padel/tambah-game`,
    padelEditSkor: (slug: string, gameSlug: string) =>
      `/event/${slug}/padel/game/${gameSlug}/skor`,
    pickleball: (slug: string) => `/event/${slug}/pickleball`,
    manageShare: (slug: string) => `/event/${slug}/manage-share`,
    export: (slug: string) => `/event/${slug}/export`,
    game: (slug: string, gameSlug: string) => `/event/${slug}/game/${gameSlug}`,
  },
  arena: {
    detail: (slug: string) => `/arena/${slug}`,
    payment: '/arena/payment',
    checkout: '/arena/checkout',
    paymentSuccess: '/arena/payment-success',
  },
  discovery: {
    root: '/discovery',
    event: '/discovery/event',
    community: '/discovery/community',
    communityDetail: (slug: string) => `/discovery/community/${slug}`,
    communityMembers: (slug: string) => `/discovery/community/${slug}/members`,
    communityEvents: (slug: string) => `/discovery/community/${slug}/events`,
    arena: '/discovery/arena',
    arenaDetail: (slug: string) => `/discovery/arena/${slug}`,
  },
  community: {
    detail: (slug: string) => `/community/${slug}`,
    members: (slug: string) => `/community/${slug}/members`,
    events: (slug: string) => `/community/${slug}/events`,
  },
  booking: {
    list: '/booking',
    detail: (invoiceNumber: string) => `/booking/${invoiceNumber}`,
  },
  profile: {
    settings: '/profile',
    public: (username: string) => `/profile/${username}`,
    account: '/profile/account',
    manageCommunities: '/profile/account/communities',
    accountDelete: '/profile/account/delete',
    password: '/profile/password',
    stats: '/profile/stats',
    mmr: '/profile/mmr',
  },
  mmr: {
    leaderboard: '/mmr/leaderboard',
  },
  notification: '/notification',
  transactions: {
    list: '/transactions',
    booking: (invoiceNumber: string) => `/transactions/booking/${invoiceNumber}`,
    event: (orderNumber: string) => `/transactions/event/${orderNumber}`,
    detail: '/transactions/detail',
  },
} as const;

export const CUSTOMER_PATH_PREFIXES = [
  clientPaths.home,
  '/client', // legacy home; still accepted as returnUrl / deep link
  clientPaths.event.list,
  '/arena',
  clientPaths.booking.list,
  clientPaths.profile.settings,
  clientPaths.discovery.root,
  clientPaths.transactions.list,
  clientPaths.myCommunity,
  '/mmr',
  '/community',
  clientPaths.notification,
] as const;

export const PROFILE_RESERVED_SEGMENTS = [
  'account',
  'password',
  'stats',
  'mmr',
] as const;

export function isCustomerAppPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  // Legacy home path kept for deep links / returnUrl until fully retired
  if (path === '/client') return true;
  return CUSTOMER_PATH_PREFIXES.some((prefix) => isPathPrefix(path, prefix));
}

export function isCustomerTabRoot(pathname: string): boolean {
  const path = normalizePathname(pathname);
  if (path === clientPaths.home) return true;
  if (path === clientPaths.event.list) return true;
  if (path === clientPaths.booking.list) return true;
  if (path === clientPaths.profile.settings) return true;
  if (path === clientPaths.discovery.root) return true;
  if (path === clientPaths.discovery.event) return true;
  if (path === clientPaths.discovery.community) return true;
  if (path === clientPaths.discovery.arena) return true;
  return false;
}

export function isClientHome(pathname: string): boolean {
  return isPath(pathname, clientPaths.home);
}

export function isEventStatistikPath(pathname: string): boolean {
  return /\/event\/[^/]+\/statistik$/.test(normalizePathname(pathname));
}

export function isEventGamePath(pathname: string): boolean {
  return /\/event\/[^/]+\/game\//.test(normalizePathname(pathname));
}

export function getEventSlugFromGamePath(pathname: string): string | null {
  const match = normalizePathname(pathname).match(/\/event\/([^/]+)\/game\//);
  return match?.[1] ?? null;
}

export function isPublicProfilePath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  const match = path.match(/^\/profile\/([^/]+)$/);
  if (!match) return false;
  return !(PROFILE_RESERVED_SEGMENTS as readonly string[]).includes(match[1]);
}

export function isCommunityDetailPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return (
    /^\/community\/[^/]+$/.test(path) ||
    /^\/discovery\/community\/[^/]+$/.test(path)
  );
}

export function shouldHideClientBottomNav(
  pathname: string,
  isCommunityUser = false
): boolean {
  const path = normalizePathname(pathname);
  if (isPublicProfilePath(path) && isCommunityUser) return true;
  if (isPathPrefix(path, clientPaths.profile.stats)) return true;
  if (isPathPrefix(path, clientPaths.profile.password)) return true;
  if (isPathPrefix(path, clientPaths.profile.account)) return true;
  if (isPathPrefix(path, clientPaths.profile.mmr)) return true;
  if (isPathPrefix(path, clientPaths.mmr.leaderboard)) return true;
  if (isPathPrefix(path, clientPaths.event.create)) return true;
  if (path.includes('/mabar/create')) return true;
  if (path.startsWith('/event/edit/')) return true;
  if (path.startsWith('/event/dashboard/')) return true;
  if (path.includes('/manage-share')) return true;
  if (path.includes('/statistik')) return true;
  if (path.includes('/export')) return true;
  if (/\/event\/[^/]+\/padel/.test(path)) return true;
  if (/\/event\/[^/]+\/pickleball/.test(path)) return true;
  if (isEventGamePath(path)) return true;
  if (isPathPrefix(path, clientPaths.event.list) && path !== clientPaths.event.list) return true;
  if (isPathPrefix(path, clientPaths.booking.list) && path !== clientPaths.booking.list) return true;
  if (isPathPrefix(path, clientPaths.transactions.list)) return true;
  if (isPathPrefix(path, '/arena')) return true;
  if (isPathPrefix(path, clientPaths.discovery.arena) && path !== clientPaths.discovery.arena) {
    return true;
  }
  if (path.includes('/members')) return true;
  if (/\/events$/.test(path) || path.includes('/events/')) return true;
  if (isPathPrefix(path, '/community') || isCommunityDetailPath(path)) return true;
  if (isPath(path, clientPaths.notification)) return true;
  return false;
}
